import { Component, OnDestroy, OnInit } from '@angular/core';

import { combineLatest, filter, Subscription, switchMap, take, tap } from 'rxjs';

import { makeMap } from '@shared/core';
import { Location } from '@shared/location/models';
import { PricePlan } from '@shared/price-plan/models';
import { IWaitingPartiesResponse } from '@shared/party/models';
import { AuthUser } from '@shared/signup/models';
import { AccountUser, LocationUser } from '@shared/user/models';
import { UserAccountRole } from '@shared/user/enums';
import { SubscriptionTypes } from '@shared/restaurant/enums';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { LocationSwitcherService } from '@tablesready/frontend-location-switcher';
import { SubscriptionService } from '../../billing/services';
import {
  PartiesService,
  AccountUsersService,
  LocationUsersService,
} from '../../../services';
import { AddEditLocationComponent } from '../../../modals';

@Component({
  selector: 'tablesready-location-info',
  templateUrl: './location-info.component.html',
  styleUrls: ['./location-info.component.scss'],
})
export class LocationInfoComponent implements OnInit, OnDestroy {
  user: AuthUser;
  icon = AppIcon;
  location: Location;
  color = AppIconColor;
  waitlistedParties: IWaitingPartiesResponse[] = [];
  locationUsers: LocationUser[];
  accountUsersMap: Record<string, AccountUser> = {};
  usersCount: number = 0;
  currentPricePlan: PricePlan;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly partyService: PartiesService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly locationUsersService: LocationUsersService,
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly accountUsersService: AccountUsersService,
    private readonly subscriptionService: SubscriptionService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.authRestaurantService.select().pipe(
          filter(user => !!user.location),
          tap(user => {
            this.user = user;
            this.location = user.location;
          }),
          switchMap(user => this.partyService.getWaitlistedParties([user.location._id])),
          tap(waitlistedParties => this.waitlistedParties = waitlistedParties),
        ),
        this.authRestaurantService.selectWithAccountAndLocation().pipe(
          filter(({ account }) => account.subscriptionType === SubscriptionTypes.recurly),
          switchMap(({ location }) => this.subscriptionService.checkSubscription(location.recurly.subscriptionUuid)),
          tap(pricePlan => this.currentPricePlan = pricePlan),
        ),
        this.authRestaurantService.selectAndDetectLocationChanges().pipe(
          switchMap(() => this.locationUsersService.load()),
          switchMap(() => this.locationUsersService.selectAll()),
        ),
        this.authRestaurantService.selectAndDetectAccountChanges().pipe(
          switchMap(() => this.accountUsersService.load()),
          switchMap(() => this.accountUsersService.selectAll()),
          tap(accountUsers => this.accountUsersMap = makeMap(accountUsers, 'userId')),
        ),
      ])
        .pipe(
          tap(([, , locationUsers]) => {
            this.usersCount = locationUsers.filter(locationUser => locationUser.locationId === this.user.location._id
              && this.accountUsersMap[locationUser.userId]?.role === UserAccountRole.user).length;
          }),
        ).subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  editLocation(): void {
    this.modalController.create<AddEditLocationComponent, unknown>(AddEditLocationComponent, {
      location: this.location,
      account: this.user.account,
    })
      .pipe(take(1))
      .subscribe();
  }

  navigateToLocationPage(locationId: string, url: string): void {
    this.locationSwitcherService.navigateToLocationPage(locationId, url);
  }

  getWaitlistedPartiesByLocation(locationId: string): number {
    return this.waitlistedParties.find(party => party.id === locationId)?.count || 0;
  }
}
